import { currentLocale } from '../i18n';

/**
 * Browser-owned Insights config: North Star and the last operational scope.
 *
 * North Star specs are persisted in localStorage and attached to every
 * `/api/call` / `/api/ask/stream` request as `clientConfig` so the backend
 * can evaluate North Star without server-side storage.
 *
 * Venue groups are no longer kept here: they are the person's own, kept on
 * the server per person and customer (decision 7.7.2, `opsCommand/
 * getScopeConfig`, `saveScopeGroup`, `deleteScopeGroup`). The groups a
 * browser kept before move to the server once (`readLegacyScopeGroups`,
 * `opsCommand/importScopeGroups`), then the keys are removed.
 */

export type ClientNorthStar = {
  label?: string;
  description?: string;
  spec: unknown;
  baseline?: unknown;
  plan?: unknown;
  history?: unknown[];
  fleetKey?: string;
};

export type ClientConfigPayload = {
  northStar: ClientNorthStar | null;
  locale: 'en' | 'de';
};

/** Former localStorage keys of the venue groups and their recipes (read for the move only). */
export const LEGACY_SCOPE_GROUPS_KEY = 'insights:scopeGroups';
export const LEGACY_SCOPE_GROUP_RECIPES_KEY = 'insights:scopeGroupRecipes';
const NORTH_STAR_KEY = 'insights:northStar';
const LAST_SCOPE_KEY_PREFIX = 'insights:lastScope:';

/**
 * The scratch group the Topbar's multi-select used to write before a set of
 * venues became its own filter (`venues:`, lib/scopeFilter.ts). A saved
 * scope or link naming it reads as those venues once, then never again.
 */
export const CURRENT_SELECTION_GROUP_ID = 'current-selection';

/** Last operational scope for a user (per-browser localStorage). */
export type ClientLastScope = {
  restaurantId: string;
  /** `all` | `org` | `group:{id}` | `outlet:{id}` | `venues:{ids}` | null */
  regionFilter: string | null;
};

function lastScopeKey(userId: string): string {
  return `${LAST_SCOPE_KEY_PREFIX}${userId}`;
}

export function getLocalLastScope(userId: string): ClientLastScope | null {
  if (!userId) return null;
  const raw = readJson<unknown>(lastScopeKey(userId), null);
  if (!raw || typeof raw !== 'object') return null;
  const restaurantId = (raw as ClientLastScope).restaurantId;
  const regionFilter = (raw as ClientLastScope).regionFilter ?? null;
  if (typeof restaurantId !== 'string' || !restaurantId) return null;
  if (regionFilter != null && typeof regionFilter !== 'string') return null;
  return { restaurantId, regionFilter };
}

export function saveLocalLastScope(
  userId: string,
  scope: ClientLastScope,
): void {
  if (!userId || !scope.restaurantId) return;
  writeJson(lastScopeKey(userId), {
    restaurantId: scope.restaurantId,
    regionFilter: scope.regionFilter ?? 'all',
  });
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or private mode: the value still works for this session.
  }
}

/** A venue group as a browser kept it before the move to the server. */
export type LegacyScopeGroup = {
  id: string;
  name: string;
  restaurantIds: string[];
  recipe?: unknown;
};

/**
 * The venue groups this browser still keeps (with their recipes), for the
 * one-time move to the server. The scratch group of the multi-select is not
 * a group and is left out (`legacyCurrentSelectionIds`).
 */
export function readLegacyScopeGroups(): LegacyScopeGroup[] {
  const raw = readJson<unknown>(LEGACY_SCOPE_GROUPS_KEY, []);
  if (!Array.isArray(raw)) return [];
  const recipes = readJson<Record<string, unknown> | null>(LEGACY_SCOPE_GROUP_RECIPES_KEY, null);
  return raw
    .filter(
      (g): g is LegacyScopeGroup =>
        !!g &&
        typeof g === 'object' &&
        typeof (g as LegacyScopeGroup).id === 'string' &&
        typeof (g as LegacyScopeGroup).name === 'string' &&
        Array.isArray((g as LegacyScopeGroup).restaurantIds) &&
        (g as LegacyScopeGroup).id !== CURRENT_SELECTION_GROUP_ID,
    )
    .map(g => {
      const recipe = recipes && typeof recipes === 'object' ? recipes[g.id] : undefined;
      return {
        id: g.id,
        name: g.name,
        restaurantIds: [...new Set(g.restaurantIds.map(String))],
        ...(recipe ? { recipe } : {}),
      };
    });
}

/** The venues the multi-select's scratch group held, if this browser still has it. */
export function legacyCurrentSelectionIds(): string[] {
  const raw = readJson<unknown>(LEGACY_SCOPE_GROUPS_KEY, []);
  if (!Array.isArray(raw)) return [];
  const found = raw.find(
    g => !!g && typeof g === 'object' && (g as LegacyScopeGroup).id === CURRENT_SELECTION_GROUP_ID,
  ) as LegacyScopeGroup | undefined;
  return Array.isArray(found?.restaurantIds) ? [...new Set(found.restaurantIds.map(String))] : [];
}

/** True while this browser still holds groups or recipes from before the move. */
export function hasLegacyScopeGroups(): boolean {
  try {
    return (
      localStorage.getItem(LEGACY_SCOPE_GROUPS_KEY) != null ||
      localStorage.getItem(LEGACY_SCOPE_GROUP_RECIPES_KEY) != null
    );
  } catch {
    return false;
  }
}

/** Forget the browser's former groups once the server has them. */
export function clearLegacyScopeGroups(): void {
  try {
    localStorage.removeItem(LEGACY_SCOPE_GROUPS_KEY);
    localStorage.removeItem(LEGACY_SCOPE_GROUP_RECIPES_KEY);
  } catch {
    // Ignore: the next load tries again, and the server skips what it has.
  }
}

type NorthStarStore = Record<string, ClientNorthStar>;

/** Resolve North Star for a restaurant (keyed by restaurantId in localStorage). */
export function getLocalNorthStarForRestaurant(
  restaurantId: string,
): ClientNorthStar | null {
  if (!restaurantId) {
    return null;
  }
  const store = readJson<NorthStarStore>(NORTH_STAR_KEY, {});
  if (store[restaurantId]) {
    return store[restaurantId] ?? null;
  }
  // Legacy / fleet-keyed entries: any key that lists this restaurant.
  for (const [key, doc] of Object.entries(store)) {
    const members = key.split(',').filter(Boolean);
    if (members.includes(restaurantId)) {
      return doc;
    }
  }
  return null;
}

export function saveLocalNorthStar(
  restaurantId: string,
  doc: ClientNorthStar,
): void {
  const store = readJson<NorthStarStore>(NORTH_STAR_KEY, {});
  store[restaurantId] = { ...doc, fleetKey: restaurantId };
  writeJson(NORTH_STAR_KEY, store);
}

export function deleteLocalNorthStar(restaurantId: string): void {
  const store = readJson<NorthStarStore>(NORTH_STAR_KEY, {});
  delete store[restaurantId];
  writeJson(NORTH_STAR_KEY, store);
}

export function buildClientConfigPayload(
  restaurantId: string,
): ClientConfigPayload {
  return {
    northStar: getLocalNorthStarForRestaurant(restaurantId),
    locale: currentLocale(),
  };
}
